import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Wrench, UploadCloud, Loader2, AlertTriangle, CheckCircle2,
  Trash2, RefreshCw, ChevronDown, BarChart3, List,
  FileSpreadsheet, Download, Edit3, X, Save, Gauge, Calendar, Mail, XCircle, Clock,
  BellRing, History, Ban, QrCode, PenTool, Printer, Filter, Camera, Building2, ImagePlus, FileText,
} from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";
import SignatureCanvas from "react-signature-canvas";
import SendEmailModal from "../components/SendEmailModal";

const TIPOS = ["Preventivo", "Correctivo avería", "Correctivo siniestro"];
// Nº de ficheros por petición HTTP. Una carga masiva (p.ej. 80 partes) se
// fragmenta automáticamente en lotes de este tamaño para no generar una
// petición gigante ni saturar el backend; cada lote se procesa en paralelo.
const UPLOAD_CHUNK_SIZE = 20;
// Normaliza una matrícula para comparar sin espacios/guiones/mayúsculas.
const normPlate = (p) => (p || "").toUpperCase().replace(/[\s-]/g, "");

export default function Workshop() {
  const { authAxios } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [maintenanceDue, setMaintenanceDue] = useState(null); // { threshold_days, pending }
  const [view, setView] = useState("list");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // { totalFiles, totalChunks, currentChunk, processed, errors, batchIds, cancelling }
  const [batchHistory, setBatchHistory] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [matriculaFilter, setMatriculaFilter] = useState("");
  const [tipoFilter, setTipoFilter] = useState("");
  const [mecanicoFilter, setMecanicoFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null); // { kind: "pdf"|"xlsx", ticket? }
  const [historyModal, setHistoryModal] = useState(null); // { matricula, loading, tickets }
  const [qrModal, setQrModal] = useState(null); // { matricula, dataUrl }
  const [qrBulkModal, setQrBulkModal] = useState(null); // { center, vehicles, loading, generating }
  const [centersList, setCentersList] = useState([]);
  const [onlyPending, setOnlyPending] = useState(false);
  const [editingPhotos, setEditingPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [mechanics, setMechanics] = useState([]);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const pollRef = useRef(null);
  const signaturePadRef = useRef(null);
  const editingPhotoUrlsRef = useRef([]);
  const historyPhotoUrlsRef = useRef([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (matriculaFilter) params.matricula = matriculaFilter;
      if (tipoFilter) params.tipo_mantenimiento = tipoFilter;
      if (mecanicoFilter) params.mecanico = mecanicoFilter;
      const [{ data: list }, { data: dash }, { data: due }] = await Promise.all([
        authAxios.get("/workshop", { params }),
        authAxios.get("/workshop/dashboard", { params: { date_from: dateFrom, date_to: dateTo } }),
        authAxios.get("/workshop/maintenance-due"),
      ]);
      setTickets(list);
      setDashboard(dash);
      setMaintenanceDue(due);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Error cargando actuaciones del taller");
    } finally { setLoading(false); }
  };

  const loadBatchHistory = async () => {
    try {
      const { data } = await authAxios.get("/workshop/batches", { params: { limit: 8 } });
      setBatchHistory(data);
    } catch (e) { /* silencioso: no es crítico para la vista principal */ }
  };

  const openVehicleHistory = async (matricula) => {
    historyPhotoUrlsRef.current.forEach(u => URL.revokeObjectURL(u));
    historyPhotoUrlsRef.current = [];
    setHistoryModal({ matricula, loading: true, tickets: [] });
    try {
      const { data } = await authAxios.get("/workshop/vehicle-history", { params: { matricula } });
      const withPhotos = await Promise.all(data.map(async (t) => {
        try {
          const { data: photoList } = await authAxios.get(`/workshop/${t.id}/photos`);
          const photos = await Promise.all(photoList.map(async (p) => {
            const res = await authAxios.get(`/workshop/photos/${p.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            historyPhotoUrlsRef.current.push(url);
            return { ...p, blobUrl: url };
          }));
          return { ...t, photos };
        } catch (e) { return { ...t, photos: [] }; }
      }));
      setHistoryModal({ matricula, loading: false, tickets: withPhotos });
    } catch (e) {
      toast.error("No se pudo cargar el historial de la máquina");
      setHistoryModal(null);
    }
  };

  const closeHistoryModal = () => {
    historyPhotoUrlsRef.current.forEach(u => URL.revokeObjectURL(u));
    historyPhotoUrlsRef.current = [];
    setHistoryModal(null);
  };

  useEffect(() => {
    authAxios.get("/workshop/centers").then(r => setCentersList(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openQrBulkModal = () => setQrBulkModal({ center: "", vehicles: [], loading: false, generating: false });

  const selectBulkCenter = async (center) => {
    if (!center) { setQrBulkModal({ center: "", vehicles: [], loading: false, generating: false }); return; }
    setQrBulkModal({ center, vehicles: [], loading: true, generating: false });
    try {
      const { data } = await authAxios.get("/workshop/vehicles-by-center", { params: { center } });
      setQrBulkModal({ center, vehicles: data, loading: false, generating: false });
    } catch (e) {
      toast.error("No se pudieron cargar las máquinas de ese centro");
      setQrBulkModal({ center, vehicles: [], loading: false, generating: false });
    }
  };

  const generateAndPrintBulkQr = async () => {
    if (!qrBulkModal?.vehicles?.length) return;
    setQrBulkModal(prev => ({ ...prev, generating: true }));
    try {
      const labels = await Promise.all(qrBulkModal.vehicles.map(async (v) => {
        const url = `${window.location.origin}/workshop?matricula=${encodeURIComponent(v.plate)}`;
        const dataUrl = await QRCode.toDataURL(url, { width: 240, margin: 1, color: { dark: "#1E293B", light: "#FFFFFF" } });
        return { plate: v.plate, dataUrl };
      }));
      const win = window.open("", "_blank");
      const labelsHtml = labels.map(l => `
        <div style="display:inline-block;width:180px;text-align:center;padding:12px;border:1px dashed #cbd5e1;margin:6px;page-break-inside:avoid;">
          <img src="${l.dataUrl}" style="width:140px;height:140px;" />
          <p style="font-family:monospace;font-weight:700;font-size:14px;margin:6px 0 0;">${l.plate}</p>
        </div>
      `).join("");
      win.document.write(`
        <html><head><title>Etiquetas QR — ${qrBulkModal.center}</title></head>
        <body style="font-family:sans-serif;text-align:center;margin:20px;">
          <h2 style="margin:0 0 16px;">${qrBulkModal.center} — ${labels.length} máquina(s)</h2>
          <div>${labelsHtml}</div>
        </body></html>
      `);
      win.document.close();
      win.focus();
      win.print();
      toast.success(`${labels.length} etiqueta(s) generada(s)`);
    } catch (e) {
      toast.error("No se pudieron generar las etiquetas QR");
    } finally {
      setQrBulkModal(prev => prev ? { ...prev, generating: false } : prev);
    }
  };

  // Deep link (usado por las etiquetas QR): /workshop?matricula=XXX abre directamente
  // el historial de esa máquina, ideal para escanear desde el móvil/tablet en el taller.
  useEffect(() => {
    const plate = searchParams.get("matricula");
    if (plate) {
      openVehicleHistory(plate);
      setSearchParams({}, { replace: true }); // evita reabrir el modal en navegaciones internas posteriores
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link (usado por el enlace "Ver manuscrito" del Excel exportado):
  // /workshop?original_document=TICKET_ID abre directamente el documento original.
  useEffect(() => {
    const ticketId = searchParams.get("original_document");
    if (ticketId) {
      viewOriginalDocument(ticketId);
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openQrModal = async (matricula) => {
    try {
      const url = `${window.location.origin}/workshop?matricula=${encodeURIComponent(matricula)}`;
      const dataUrl = await QRCode.toDataURL(url, { width: 320, margin: 2, color: { dark: "#1E293B", light: "#FFFFFF" } });
      setQrModal({ matricula, dataUrl });
    } catch (e) {
      toast.error("No se pudo generar el código QR");
    }
  };

  const downloadQr = () => {
    if (!qrModal) return;
    const link = document.createElement("a");
    link.href = qrModal.dataUrl;
    link.download = `QR_${normPlate(qrModal.matricula)}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const printQr = () => {
    if (!qrModal) return;
    const win = window.open("", "_blank", "width=420,height=560");
    win.document.write(`
      <html><head><title>Etiqueta QR ${qrModal.matricula}</title></head>
      <body style="margin:0;display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;">
        <img src="${qrModal.dataUrl}" style="width:280px;height:280px;" />
        <p style="font-family:monospace;font-weight:700;font-size:22px;margin-top:12px;">${qrModal.matricula}</p>
        <p style="font-size:11px;color:#64748b;">Escanea para ver el historial de esta máquina</p>
      </body></html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  useEffect(() => { refresh(); }, [dateFrom, dateTo, matriculaFilter, tipoFilter, mecanicoFilter]);
  useEffect(() => {
    loadBatchHistory();
    authAxios.get("/workshop/mechanics").then(({ data }) => setMechanics(data)).catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const viewOriginalDocument = async (ticketId) => {
    try {
      const res = await authAxios.get(`/workshop/${ticketId}/original-document`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se encontró el manuscrito original de esta actuación");
    }
  };

  const pollBatches = (batchIds) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;
    const MAX_ATTEMPTS = 400; // válvula de seguridad (~20 min), no un corte anticipado
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const results = await Promise.all(
          batchIds.map(id => authAxios.get(`/workshop/batch/${id}`).then(r => r.data))
        );
        const processed = results.reduce((s, b) => s + (b.processed || 0), 0);
        const errors = results.reduce((s, b) => s + (b.errors || 0), 0);
        const cancelled = results.reduce((s, b) => s + (b.cancelled || 0), 0);
        const duplicates = results.reduce((s, b) => s + (b.duplicates || 0), 0);
        const allDone = results.every(b => b.status !== "processing") || attempts > MAX_ATTEMPTS;
        setBulkProgress(prev => prev ? { ...prev, processed, errors, cancelled } : prev);
        loadBatchHistory();
        if (allDone) {
          clearInterval(pollRef.current);
          refresh();
          if (cancelled > 0) toast.warning(`Carga cancelada: ${processed} procesados, ${cancelled} omitidos.`);
          else if (errors === 0) toast.success(`Procesadas ${processed} actuación(es) correctamente.`);
          else toast.warning(`${processed} correctas, ${errors} con error. Revisa la lista y corrígelas si es necesario.`);
          if (duplicates > 0) {
            toast.warning(`Atención: ${duplicates} parte(s) parecen duplicados (misma matrícula y fecha que uno ya existente). Revísalos en la lista.`, { duration: 7000 });
          }
          setTimeout(() => setBulkProgress(null), 4000);
        }
      } catch (err) { clearInterval(pollRef.current); setBulkProgress(null); }
    }, 3000);
  };

  const startBulkUpload = async (filesList) => {
    if (!filesList || filesList.length === 0) return;

    const chunks = [];
    for (let i = 0; i < filesList.length; i += UPLOAD_CHUNK_SIZE) {
      chunks.push(filesList.slice(i, i + UPLOAD_CHUNK_SIZE));
    }

    setUploading(true);
    setBulkProgress({
      totalFiles: filesList.length, totalChunks: chunks.length,
      currentChunk: 0, processed: 0, errors: 0, cancelled: 0, batchIds: [],
    });

    const batchIds = [];
    try {
      for (let i = 0; i < chunks.length; i++) {
        setBulkProgress(prev => ({ ...prev, currentChunk: i + 1 }));
        const form = new FormData();
        chunks[i].forEach(f => form.append("files", f));
        const { data } = await authAxios.post("/workshop/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        batchIds.push(data.batch_id);
        setBulkProgress(prev => ({ ...prev, batchIds: [...batchIds] }));
      }
      toast.success(
        chunks.length > 1
          ? `${filesList.length} documentos enviados en ${chunks.length} lotes. Procesando...`
          : `${filesList.length} documento(s) en proceso.`
      );
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Error subiendo documentos");
    } finally {
      setUploading(false);
    }

    if (batchIds.length === 0) { setBulkProgress(null); return; }
    loadBatchHistory();
    pollBatches(batchIds);
  };

  const handleUpload = (e) => {
    const filesList = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = "";
    startBulkUpload(filesList);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const filesList = Array.from(e.dataTransfer.files || []).filter(f => {
      const n = f.name.toLowerCase();
      return n.endsWith(".pdf") || n.endsWith(".jpg") || n.endsWith(".jpeg") || n.endsWith(".png");
    });
    if (filesList.length === 0) {
      toast.error("Solo se aceptan PDF, JPG o PNG");
      return;
    }
    startBulkUpload(filesList);
  };

  const cancelActiveUpload = async () => {
    if (!bulkProgress?.batchIds?.length) return;
    setBulkProgress(prev => ({ ...prev, cancelling: true }));
    try {
      await Promise.all(
        bulkProgress.batchIds.map(id => authAxios.post(`/workshop/batch/${id}/cancel`).catch(() => null))
      );
      toast.success("Cancelando el resto de documentos pendientes...");
    } catch (e) {
      toast.error("No se pudo cancelar la carga");
    }
  };

  const cancelBatchRow = async (batchId) => {
    try {
      await authAxios.post(`/workshop/batch/${batchId}/cancel`);
      toast.success("Lote cancelado");
      loadBatchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cancelar el lote");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta actuación de taller?")) return;
    try {
      await authAxios.delete(`/workshop/${id}`);
      toast.success("Actuación eliminada");
      refresh();
    } catch (e) { toast.error("No se pudo eliminar"); }
  };

  const dismissDuplicate = async (id) => {
    try {
      await authAxios.post(`/workshop/${id}/dismiss-duplicate`);
      toast.success("Aviso de duplicado descartado");
      refresh();
    } catch (e) { toast.error("No se pudo descartar el aviso"); }
  };

  const downloadPdf = async (t) => {
    try {
      const { data } = await authAxios.get(`/workshop/${t.id}/export-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Parte_Taller_${(t.matricula || "sinmatricula").replace(/\s/g, "")}_${t.fecha || ""}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo descargar el PDF");
    }
  };

  const downloadXlsx = async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (matriculaFilter) params.matricula = matriculaFilter;
      if (mecanicoFilter) params.mecanico = mecanicoFilter;
      const { data } = await authAxios.get("/workshop/export.xlsx", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      link.download = `parque_maquinaria_actuaciones_${stamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Listado exportado a Excel");
    } catch (e) {
      toast.error("No se pudo descargar el listado");
    }
  };

  const openEdit = (t) => {
    editingPhotoUrlsRef.current.forEach(u => URL.revokeObjectURL(u));
    editingPhotoUrlsRef.current = [];
    setEditingPhotos([]);
    setEditing({
      id: t.id,
      form: {
        centro: t.centro || "",
        matricula: t.matricula || "", tipo_mantenimiento: t.tipo_mantenimiento || "",
        horas: t.horas ?? "", km: t.km ?? "",
        descripcion_averia: t.descripcion_averia || "", trabajos_realizados: t.trabajos_realizados || "",
        materiales_empleados: t.materiales_empleados || "",
        fecha: t.fecha || "", fecha_cierre: t.fecha_cierre || "", responsable: t.responsable || "",
        tiempo_total: (t.tiempo_reparacion || [])[0]?.total ?? "",
        mecanico: (t.tiempo_reparacion || [])[0]?.mecanico || t.responsable || "",
        firma: t.firma || null,
        has_original: !!t.original_document_path,
      },
    });
    loadEditingPhotos(t.id);
  };

  const loadEditingPhotos = async (ticketId) => {
    try {
      const { data: list } = await authAxios.get(`/workshop/${ticketId}/photos`);
      const withBlobs = await Promise.all(list.map(async (p) => {
        const res = await authAxios.get(`/workshop/photos/${p.id}/download`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        editingPhotoUrlsRef.current.push(url);
        return { ...p, blobUrl: url };
      }));
      setEditingPhotos(withBlobs);
    } catch (e) { setEditingPhotos([]); }
  };

  const closeEditModal = () => {
    editingPhotoUrlsRef.current.forEach(u => URL.revokeObjectURL(u));
    editingPhotoUrlsRef.current = [];
    setEditingPhotos([]);
    setEditing(null);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (photoInputRef.current) photoInputRef.current.value = "";
    if (files.length === 0 || !editing) return;
    setUploadingPhoto(true);
    const form = new FormData();
    files.forEach(f => form.append("files", f));
    try {
      await authAxios.post(`/workshop/${editing.id}/photos`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Foto(s) añadida(s)");
      await loadEditingPhotos(editing.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir la foto");
    } finally { setUploadingPhoto(false); }
  };

  const handlePhotoDelete = async (photoId) => {
    try {
      await authAxios.delete(`/workshop/photos/${photoId}`);
      setEditingPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (e) { toast.error("No se pudo eliminar la foto"); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const f = editing.form;
      await authAxios.patch(`/workshop/${editing.id}`, {
        centro: f.centro, matricula: normPlate(f.matricula),
        tipo_mantenimiento: f.tipo_mantenimiento,
        horas: f.horas === "" ? null : Number(f.horas),
        km: f.km === "" ? null : Number(f.km),
        descripcion_averia: f.descripcion_averia, trabajos_realizados: f.trabajos_realizados,
        materiales_empleados: f.materiales_empleados,
        fecha: f.fecha, fecha_cierre: f.fecha_cierre, responsable: f.responsable,
        tiempo_reparacion: [{ fecha: f.fecha, de: "", a: "", total: f.tiempo_total === "" ? null : Number(f.tiempo_total), mecanico: f.mecanico }],
        firma: f.firma,
      });
      toast.success("Actuación actualizada");
      closeEditModal();
      refresh();
      authAxios.get("/workshop/mechanics").then(({ data }) => setMechanics(data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    } finally { setSavingEdit(false); }
  };

  const captureSignature = () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) return;
    const dataUrl = signaturePadRef.current.toDataURL("image/png");
    setEditing(prev => ({ ...prev, form: { ...prev.form, firma: dataUrl } }));
  };

  const clearSignature = () => {
    if (signaturePadRef.current) signaturePadRef.current.clear();
    setEditing(prev => ({ ...prev, form: { ...prev.form, firma: null } }));
  };

  const pendingPlates = new Set((maintenanceDue?.pending || []).map(p => normPlate(p.matricula)));
  const visibleTickets = onlyPending
    ? tickets.filter(t => t.matricula && pendingPlates.has(normPlate(t.matricula)))
    : tickets;

  return (
    <div className="space-y-6" data-testid="workshop-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 flex items-center gap-3">
            <Wrench size={28} className="text-[#EA580C]" />
            Taller · Parque Central de Maquinaria
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Sube los partes de taller manuscritos (incidencias + trabajo) y la IA rellena el Parte Trabajo Taller.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <input type="date" data-testid="ws-date-from" value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs bg-white" title="Desde" />
          <span className="text-slate-400 text-xs">→</span>
          <input type="date" data-testid="ws-date-to" value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs bg-white" title="Hasta" />
          <input type="text" data-testid="ws-matricula-filter" value={matriculaFilter}
            onChange={(e) => setMatriculaFilter(e.target.value)}
            placeholder="Matrícula..."
            className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs bg-white w-28" />
          <div className="relative">
            <select data-testid="ws-tipo-filter" value={tipoFilter} onChange={(e) => setTipoFilter(e.target.value)}
              className="appearance-none pl-3 pr-8 py-1.5 border border-slate-300 rounded-lg text-xs font-medium bg-white text-slate-700">
              <option value="">Todos los tipos</option>
              {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
          <div className="relative">
            <select data-testid="ws-mecanico-filter" value={mecanicoFilter} onChange={(e) => setMecanicoFilter(e.target.value)}
              className="appearance-none pl-3 pr-8 py-1.5 border border-slate-300 rounded-lg text-xs font-medium bg-white text-slate-700 max-w-[160px]">
              <option value="">Todos los mecánicos</option>
              {mechanics.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
          <button data-testid="ws-only-pending-toggle" onClick={() => setOnlyPending(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              onlyPending ? "bg-amber-500 border-amber-500 text-white" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
            }`}>
            <Filter size={12} /> Solo pendientes
            {maintenanceDue?.pending?.length > 0 && (
              <span className={`ml-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${
                onlyPending ? "bg-white text-amber-600" : "bg-amber-500 text-white"
              }`}>{maintenanceDue.pending.length}</span>
            )}
          </button>
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button data-testid="ws-view-list" onClick={() => setView("list")}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-semibold ${view === "list" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
              <List size={14} /> Lista
            </button>
            <button data-testid="ws-view-dashboard" onClick={() => setView("dashboard")}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-semibold ${view === "dashboard" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
              <BarChart3 size={14} /> Dashboard
            </button>
          </div>
          <button data-testid="ws-refresh" onClick={refresh} disabled={loading}
            className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
          <button data-testid="ws-export-xlsx" onClick={downloadXlsx}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700">
            <FileSpreadsheet size={13} /> Exportar Excel
          </button>
          <button data-testid="ws-email-xlsx-btn" onClick={() => setEmailTarget({ kind: "xlsx" })}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50">
            <Mail size={13} /> Enviar por email
          </button>
          <button data-testid="ws-qr-bulk-btn" onClick={openQrBulkModal}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold bg-purple-600 text-white hover:bg-purple-700">
            <QrCode size={13} /> QR Masivo
          </button>
        </div>
      </div>

      {/* Upload zone */}
      <div
        data-testid="ws-upload-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`bg-white border-2 border-dashed rounded-2xl p-6 text-center transition-colors ${
          dragging ? "border-[#EA580C] bg-orange-50/50" : "border-slate-300"
        }`}
      >
        <UploadCloud size={30} className={`mx-auto mb-2 ${dragging ? "text-[#EA580C]" : "text-slate-400"}`} />
        <p className="text-sm text-slate-600 mb-3">
          Arrastra o selecciona los partes de taller manuscritos (incidencias del vehículo + trabajo realizado). PDF, JPG o PNG. Puedes seleccionar cientos de documentos a la vez: se procesan automáticamente en lotes.
        </p>
        <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleUpload} className="hidden" data-testid="ws-file-input" />
        <button data-testid="ws-upload-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#EA580C] hover:bg-[#C2410C] text-white font-semibold rounded-lg text-sm disabled:opacity-50">
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />}
          {uploading ? "Subiendo..." : "Seleccionar documentos"}
        </button>

        {bulkProgress && (
          <div className="mt-5 max-w-md mx-auto text-left" data-testid="ws-bulk-progress">
            <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-1">
              <span>
                {uploading
                  ? `Enviando lote ${bulkProgress.currentChunk}/${bulkProgress.totalChunks}...`
                  : bulkProgress.cancelling ? "Cancelando..." : "Procesando con IA..."}
              </span>
              <span>{bulkProgress.processed + bulkProgress.errors + bulkProgress.cancelled}/{bulkProgress.totalFiles}</span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#EA580C] transition-all duration-500"
                style={{ width: `${Math.min(100, ((bulkProgress.processed + bulkProgress.errors + bulkProgress.cancelled) / bulkProgress.totalFiles) * 100)}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <div className="flex items-center gap-3 text-[11px] text-slate-400">
                <span className="flex items-center gap-1"><CheckCircle2 size={11} className="text-emerald-500" /> {bulkProgress.processed} ok</span>
                {bulkProgress.errors > 0 && (
                  <span className="flex items-center gap-1 text-red-500"><XCircle size={11} /> {bulkProgress.errors} con error</span>
                )}
                {bulkProgress.cancelled > 0 && (
                  <span className="flex items-center gap-1 text-slate-500"><Ban size={11} /> {bulkProgress.cancelled} omitidos</span>
                )}
              </div>
              {!uploading && !bulkProgress.cancelling && (
                <button data-testid="ws-cancel-upload-btn" onClick={cancelActiveUpload}
                  className="text-[11px] font-semibold text-red-500 hover:text-red-600 flex items-center gap-1">
                  <Ban size={11} /> Cancelar
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Batch history */}
      {batchHistory.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="ws-batch-history">
          <h3 className="text-xs font-bold text-slate-500 uppercase mb-3 flex items-center gap-1.5">
            <Clock size={13} /> Cargas recientes
          </h3>
          <div className="space-y-1.5">
            {batchHistory.map(b => (
              <div key={b.id} className="flex items-center justify-between text-xs py-1.5 px-2 rounded-lg hover:bg-slate-50"
                data-testid={`ws-batch-row-${b.id}`}>
                <span className="text-slate-400 whitespace-nowrap">
                  {new Date(b.created_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </span>
                <span className="text-slate-600 flex-1 px-3 truncate">{b.created_by_name || "—"}</span>
                <span className="text-slate-700 font-medium whitespace-nowrap">{b.processed}/{b.total} documentos</span>
                {b.errors > 0 && <span className="text-red-500 font-medium whitespace-nowrap ml-2">{b.errors} error(es)</span>}
                <BatchStatusBadge status={b.status} />
                {b.status === "processing" && (
                  <button data-testid={`ws-batch-cancel-${b.id}`} onClick={() => cancelBatchRow(b.id)}
                    className="ml-2 text-red-500 hover:text-red-600" title="Cancelar este lote">
                    <Ban size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}


      {/* Dashboard */}
      {view === "dashboard" && dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard icon={Wrench} label="Actuaciones" value={dashboard.total_actuaciones} color="#EA580C" />
          <StatCard icon={AlertTriangle} label="Pendientes de revisión" value={dashboard.pending_review} color="#B45309" />
          <StatCard icon={Gauge} label="Matrículas distintas" value={dashboard.by_matricula?.length || 0} color="#2563EB" />
          <StatCard icon={Calendar} label="Meses con actividad" value={dashboard.by_month?.length || 0} color="#7C3AED" />

          {maintenanceDue && (
            <div className="md:col-span-4 card-white p-5" data-testid="ws-maintenance-due-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <BellRing size={16} className="text-amber-600" /> Aviso por vencimiento
                </h3>
                <span className="text-[11px] text-slate-400">
                  Sin mantenimiento preventivo desde hace más de {maintenanceDue.threshold_days} días
                </span>
              </div>
              {(!maintenanceDue.pending || maintenanceDue.pending.length === 0) ? (
                <p className="text-xs text-slate-400 flex items-center gap-1.5">
                  <CheckCircle2 size={14} className="text-emerald-500" /> Todas las máquinas están al día.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {maintenanceDue.pending.map(p => (
                    <button key={p.matricula} data-testid={`ws-maintenance-due-${p.matricula}`}
                      onClick={() => openVehicleHistory(p.matricula)}
                      className="flex items-center justify-between px-3 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 text-left transition-colors">
                      <span className="font-mono text-xs font-bold text-slate-700">{p.matricula}</span>
                      <span className="text-[11px] font-semibold text-amber-700 whitespace-nowrap ml-2">
                        {p.days_since === null ? "Nunca revisada" : `${p.days_since} días`}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="md:col-span-2 card-white p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-3">Top matrículas</h3>
            {(!dashboard.by_matricula || dashboard.by_matricula.length === 0) ? (
              <p className="text-xs text-slate-400">Sin datos</p>
            ) : (
              <div className="space-y-2">
                {dashboard.by_matricula.map(m => (
                  <button key={m.matricula} data-testid={`ws-top-matricula-${m.matricula}`}
                    onClick={() => openVehicleHistory(m.matricula)}
                    className="w-full flex items-center justify-between text-sm hover:bg-slate-50 rounded px-1.5 -mx-1.5 py-0.5">
                    <span className="font-mono font-medium text-slate-700 flex items-center gap-1.5">
                      <History size={12} className="text-slate-400" /> {m.matricula}
                    </span>
                    <span className="font-bold text-[#EA580C]">{m.count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="md:col-span-2 card-white p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-3">Por tipo de mantenimiento</h3>
            {(!dashboard.by_tipo || dashboard.by_tipo.length === 0) ? (
              <p className="text-xs text-slate-400">Sin datos</p>
            ) : (
              <div className="space-y-2">
                {dashboard.by_tipo.map(t => (
                  <div key={t.tipo} className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700">{t.tipo}</span>
                    <span className="font-bold text-slate-800">{t.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}


      {/* List */}
      {view === "list" && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {visibleTickets.length === 0 ? (
            <div className="p-10 text-center">
              <Wrench size={32} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">
                {onlyPending ? "No hay máquinas pendientes con actuaciones en este listado." : "Aún no hay actuaciones de taller. Sube los primeros documentos arriba."}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="ws-tickets-table">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Fecha</th>
                  <th className="text-left px-4 py-3">Matrícula</th>
                  <th className="text-left px-4 py-3">Centro</th>
                  <th className="text-left px-4 py-3">Tipo</th>
                  <th className="text-left px-4 py-3">Trabajos realizados</th>
                  <th className="text-right px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visibleTickets.map(t => (
                  <tr key={t.id} className={`border-t border-slate-100 hover:bg-slate-50 ${t.duplicate_of ? "bg-rose-50/60" : (!t.matricula || !t.trabajos_realizados) ? "bg-amber-50/50" : ""}`}
                      data-testid={`ws-ticket-row-${t.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-700 whitespace-nowrap">{t.fecha || "—"}</td>
                    <td className="px-4 py-3 font-mono text-slate-800">
                      {t.matricula ? (
                        <button data-testid={`ws-history-link-${t.id}`} onClick={() => openVehicleHistory(t.matricula)}
                          className="hover:underline hover:text-[#EA580C] flex items-center gap-1" title="Ver historial de esta máquina">
                          {t.matricula}
                        </button>
                      ) : (
                        <span className="text-amber-600 flex items-center gap-1"><AlertTriangle size={12} />sin matrícula</span>
                      )}
                      {t.duplicate_of && (
                        <span data-testid={`ws-duplicate-badge-${t.id}`} className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-rose-600">
                          <AlertTriangle size={10} /> Posible duplicado
                          <button data-testid={`ws-duplicate-dismiss-${t.id}`} onClick={() => dismissDuplicate(t.id)}
                            className="underline hover:text-rose-800 ml-1" title="No es un duplicado, descartar aviso">
                            descartar
                          </button>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs truncate max-w-[160px]" title={t.centro}>{t.centro || "—"}</td>
                    <td className="px-4 py-3">
                      {t.tipo_mantenimiento ? (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 text-xs font-semibold">{t.tipo_mantenimiento}</span>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 truncate max-w-[280px]" title={t.trabajos_realizados}>{t.trabajos_realizados || "—"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button data-testid={`ws-edit-${t.id}`} onClick={() => openEdit(t)}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-600 mr-1" title="Editar">
                        <Edit3 size={14} />
                      </button>
                      {t.original_document_path && (
                        <button data-testid={`ws-view-original-${t.id}`} onClick={() => viewOriginalDocument(t.id)}
                          className="p-1.5 rounded hover:bg-teal-50 text-teal-600 mr-1" title="Ver manuscrito original (respaldo)">
                          <FileText size={14} />
                        </button>
                      )}
                      {t.matricula && (
                        <button data-testid={`ws-qr-${t.id}`} onClick={() => openQrModal(t.matricula)}
                          className="p-1.5 rounded hover:bg-purple-50 text-purple-600 mr-1" title="Generar etiqueta QR de esta máquina">
                          <QrCode size={14} />
                        </button>
                      )}
                      <button data-testid={`ws-download-pdf-${t.id}`} onClick={() => downloadPdf(t)}
                        className="p-1.5 rounded hover:bg-orange-50 text-orange-600 mr-1" title="Descargar Parte Trabajo Taller (PDF)">
                        <Download size={14} />
                      </button>
                      <button data-testid={`ws-email-pdf-${t.id}`} onClick={() => setEmailTarget({ kind: "pdf", ticket: t })}
                        className="p-1.5 rounded hover:bg-blue-50 text-blue-600 mr-1" title="Enviar parte por email">
                        <Mail size={14} />
                      </button>
                      <button data-testid={`ws-delete-${t.id}`} onClick={() => handleDelete(t.id)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Eliminar">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" data-testid="ws-edit-modal"
          onClick={() => !savingEdit && closeEditModal()}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <Edit3 size={18} className="text-[#EA580C]" /> Editar Parte Trabajo Taller
              </h3>
              <div className="flex items-center gap-1">
                {editing.form.has_original && (
                  <button data-testid="ws-edit-view-original" onClick={() => viewOriginalDocument(editing.id)}
                    className="p-1.5 rounded hover:bg-teal-50 text-teal-600" title="Ver manuscrito original">
                    <FileText size={16} />
                  </button>
                )}
                <button onClick={closeEditModal} className="p-1 rounded hover:bg-slate-100 text-slate-500">
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Field label="Centro" testid="ws-edit-centro" value={editing.form.centro}
                    onChange={v => setEditing({ ...editing, form: { ...editing.form, centro: v } })} />
                </div>
                <Field label="Matrícula" testid="ws-edit-matricula" value={editing.form.matricula} mono
                  onChange={v => setEditing({ ...editing, form: { ...editing.form, matricula: normPlate(v) } })} />
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Tipo mantenimiento</label>
                  <select data-testid="ws-edit-tipo" value={editing.form.tipo_mantenimiento}
                    onChange={e => setEditing({ ...editing, form: { ...editing.form, tipo_mantenimiento: e.target.value } })}
                    className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm bg-white">
                    <option value="">—</option>
                    {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <Field label="Fecha" testid="ws-edit-fecha" type="date" value={editing.form.fecha}
                  onChange={v => setEditing({ ...editing, form: { ...editing.form, fecha: v } })} />
                <Field label="Horas vehículo" testid="ws-edit-horas" type="number" value={editing.form.horas}
                  onChange={v => setEditing({ ...editing, form: { ...editing.form, horas: v } })} />
                <Field label="Km vehículo" testid="ws-edit-km" type="number" value={editing.form.km}
                  onChange={v => setEditing({ ...editing, form: { ...editing.form, km: v } })} />
              </div>
              <TextArea label="Descripción avería" testid="ws-edit-descripcion" value={editing.form.descripcion_averia}
                onChange={v => setEditing({ ...editing, form: { ...editing.form, descripcion_averia: v } })} />
              <TextArea label="Trabajos realizados" testid="ws-edit-trabajos" rows={3} value={editing.form.trabajos_realizados}
                onChange={v => setEditing({ ...editing, form: { ...editing.form, trabajos_realizados: v } })} />
              <TextArea label="Materiales empleados" testid="ws-edit-materiales" value={editing.form.materiales_empleados}
                onChange={v => setEditing({ ...editing, form: { ...editing.form, materiales_empleados: v } })} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Tiempo invertido (horas)" testid="ws-edit-tiempo" type="number" step="0.5" min="0"
                  value={editing.form.tiempo_total}
                  onChange={v => setEditing({ ...editing, form: { ...editing.form, tiempo_total: v } })} />
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Mecánico</label>
                  <input
                    data-testid="ws-edit-mecanico"
                    list="ws-mechanics-list"
                    value={editing.form.mecanico}
                    onChange={e => setEditing({ ...editing, form: { ...editing.form, mecanico: e.target.value } })}
                    placeholder="Elige de la lista o escribe uno nuevo"
                    className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
                  />
                  <datalist id="ws-mechanics-list">
                    {mechanics.map(m => <option key={m.id} value={m.name} />)}
                  </datalist>
                </div>
                <Field label="Fecha cierre" testid="ws-edit-fecha-cierre" type="date" value={editing.form.fecha_cierre}
                  onChange={v => setEditing({ ...editing, form: { ...editing.form, fecha_cierre: v } })} />
                <Field label="Responsable" testid="ws-edit-responsable" value={editing.form.responsable}
                  onChange={v => setEditing({ ...editing, form: { ...editing.form, responsable: v } })} />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-1.5">
                  <PenTool size={12} /> Firma (al cerrar la reparación)
                </label>
                {editing.form.firma ? (
                  <div className="border border-slate-200 rounded-lg p-3 flex items-center justify-between bg-slate-50" data-testid="ws-signature-preview">
                    <img src={editing.form.firma} alt="Firma" className="h-16 bg-white rounded border border-slate-200 px-2" />
                    <button data-testid="ws-signature-redo" onClick={clearSignature}
                      className="text-xs font-semibold text-slate-500 hover:text-red-500">
                      Rehacer firma
                    </button>
                  </div>
                ) : (
                  <div className="border border-dashed border-slate-300 rounded-lg overflow-hidden">
                    <SignatureCanvas
                      ref={signaturePadRef}
                      penColor="#1E293B"
                      canvasProps={{ className: "w-full h-32 bg-white touch-none", "data-testid": "ws-signature-pad" }}
                      onEnd={captureSignature}
                    />
                    <div className="flex justify-end px-2 py-1 bg-slate-50 border-t border-slate-200">
                      <button data-testid="ws-signature-clear" onClick={clearSignature}
                        className="text-[11px] font-semibold text-slate-500 hover:text-red-500">
                        Limpiar
                      </button>
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-slate-400 mt-1">Firma con el dedo o el stylus directamente en la tablet/móvil del taller.</p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1.5">
                    <Camera size={12} /> Fotos de la avería / reparación
                  </label>
                  <input ref={photoInputRef} type="file" multiple accept="image/*"
                    onChange={handlePhotoUpload} className="hidden" data-testid="ws-photo-input" />
                  <button data-testid="ws-photo-add-btn" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#EA580C] hover:text-[#C2410C] disabled:opacity-50">
                    {uploadingPhoto ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />}
                    Añadir fotos
                  </button>
                </div>
                {editingPhotos.length === 0 ? (
                  <p className="text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg py-4 text-center">
                    Sin fotos adjuntas todavía.
                  </p>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="ws-photo-gallery">
                    {editingPhotos.map(p => (
                      <div key={p.id} className="relative group" data-testid={`ws-photo-thumb-${p.id}`}>
                        <a href={p.blobUrl} target="_blank" rel="noopener noreferrer">
                          <img src={p.blobUrl} alt={p.original_filename} className="w-full h-20 object-cover rounded-lg border border-slate-200" />
                        </a>
                        <button data-testid={`ws-photo-delete-${p.id}`} onClick={() => handlePhotoDelete(p.id)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <X size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
              <button onClick={closeEditModal} disabled={savingEdit}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-slate-100">
                Cancelar
              </button>
              <button data-testid="ws-edit-save" onClick={saveEdit} disabled={savingEdit}
                className="inline-flex items-center gap-1 px-4 py-1.5 rounded-lg text-sm font-semibold bg-[#EA580C] text-white hover:bg-[#C2410C] disabled:opacity-50">
                {savingEdit ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      <SendEmailModal
        open={!!emailTarget}
        onClose={() => setEmailTarget(null)}
        title={emailTarget?.kind === "pdf" ? "Enviar Parte Trabajo Taller" : "Enviar listado de actuaciones"}
        attachmentLabel={emailTarget?.kind === "pdf"
          ? `Parte_Taller_${(emailTarget?.ticket?.matricula || "sinmatricula").replace(/\s/g, "")}.pdf`
          : "parque_maquinaria_actuaciones.xlsx"}
        onSend={async ({ to, message }) => {
          if (emailTarget?.kind === "pdf") {
            await authAxios.post(`/workshop/${emailTarget.ticket.id}/email-pdf`, { to, message });
          } else {
            const params = {};
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            if (matriculaFilter) params.matricula = matriculaFilter;
            if (mecanicoFilter) params.mecanico = mecanicoFilter;
            await authAxios.post("/workshop/email-xlsx", { to, message, ...params });
          }
        }}
      />

      {/* Vehicle history modal */}
      {historyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" data-testid="ws-history-modal"
          onClick={closeHistoryModal}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <History size={18} className="text-[#EA580C]" /> Historial de {historyModal.matricula}
              </h3>
              <div className="flex items-center gap-1">
                <button data-testid="ws-history-qr-btn" onClick={() => openQrModal(historyModal.matricula)}
                  className="p-1.5 rounded hover:bg-purple-50 text-purple-600" title="Generar etiqueta QR de esta máquina">
                  <QrCode size={16} />
                </button>
                <button onClick={closeHistoryModal} className="p-1 rounded hover:bg-slate-100 text-slate-500">
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="p-5">
              {historyModal.loading ? (
                <div className="flex items-center justify-center py-10 text-slate-400 gap-2">
                  <Loader2 size={18} className="animate-spin" /> Cargando historial...
                </div>
              ) : historyModal.tickets.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">Sin actuaciones registradas para esta matrícula.</p>
              ) : (
                <div className="space-y-3">
                  {historyModal.tickets.map(t => (
                    <div key={t.id} data-testid={`ws-history-item-${t.id}`} className="border border-slate-200 rounded-xl p-3.5">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                          <Calendar size={13} className="text-slate-400" /> {t.fecha || "—"}
                        </span>
                        {t.tipo_mantenimiento && (
                          <span className="inline-block px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 text-[11px] font-semibold">{t.tipo_mantenimiento}</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mb-1"><strong>Centro:</strong> {t.centro || "—"}</p>
                      {t.descripcion_averia && <p className="text-xs text-slate-600 mb-1"><strong>Avería:</strong> {t.descripcion_averia}</p>}
                      {t.trabajos_realizados && <p className="text-xs text-slate-600 mb-1"><strong>Trabajos:</strong> {t.trabajos_realizados}</p>}
                      {t.responsable && <p className="text-xs text-slate-400"><strong>Responsable:</strong> {t.responsable}</p>}
                      {t.firma && (
                        <div className="mt-1.5">
                          <img src={t.firma} alt="Firma" className="h-10 bg-slate-50 rounded border border-slate-200 px-1.5" />
                        </div>
                      )}
                      {t.photos?.length > 0 && (
                        <div className="flex gap-1.5 mt-2 flex-wrap" data-testid={`ws-history-photos-${t.id}`}>
                          {t.photos.map(p => (
                            <a key={p.id} href={p.blobUrl} target="_blank" rel="noopener noreferrer">
                              <img src={p.blobUrl} alt={p.original_filename} className="w-14 h-14 object-cover rounded-lg border border-slate-200" />
                            </a>
                          ))}
                        </div>
                      )}
                      <div className="flex justify-end gap-3 mt-2">
                        {t.original_document_path && (
                          <button data-testid={`ws-history-view-original-${t.id}`} onClick={() => viewOriginalDocument(t.id)}
                            className="text-[11px] font-semibold text-teal-600 hover:text-teal-700 flex items-center gap-1">
                            <FileText size={12} /> Ver manuscrito
                          </button>
                        )}
                        <button data-testid={`ws-history-download-${t.id}`} onClick={() => downloadPdf(t)}
                          className="text-[11px] font-semibold text-[#EA580C] hover:text-[#C2410C] flex items-center gap-1">
                          <Download size={12} /> Descargar PDF
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* QR label modal */}
      {qrModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" data-testid="ws-qr-modal"
          onClick={() => setQrModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <QrCode size={18} className="text-purple-600" /> Etiqueta QR
              </h3>
              <button onClick={() => setQrModal(null)} className="p-1 rounded hover:bg-slate-100 text-slate-500">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 flex flex-col items-center">
              <img src={qrModal.dataUrl} alt={`QR ${qrModal.matricula}`} className="w-56 h-56 border border-slate-200 rounded-lg" data-testid="ws-qr-image" />
              <p className="font-mono font-bold text-lg text-slate-800 mt-3">{qrModal.matricula}</p>
              <p className="text-xs text-slate-400 text-center mt-1">
                Pega esta etiqueta en la máquina. Al escanearla con el móvil se abre directamente su historial de taller.
              </p>
              <div className="flex gap-2 w-full mt-5">
                <button data-testid="ws-qr-download" onClick={downloadQr}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg text-sm">
                  <Download size={14} /> Descargar
                </button>
                <button data-testid="ws-qr-print" onClick={printQr}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold rounded-lg text-sm">
                  <Printer size={14} /> Imprimir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk QR modal (todas las máquinas de un centro) */}
      {qrBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" data-testid="ws-qr-bulk-modal"
          onClick={() => setQrBulkModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <QrCode size={18} className="text-purple-600" /> QR Masivo por centro
              </h3>
              <button onClick={() => setQrBulkModal(null)} className="p-1 rounded hover:bg-slate-100 text-slate-500">
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <label className="text-xs font-bold text-slate-500 uppercase mb-1.5 flex items-center gap-1.5">
                <Building2 size={12} /> Centro operativo
              </label>
              <select data-testid="ws-qr-bulk-center-select" value={qrBulkModal.center}
                onChange={(e) => selectBulkCenter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-4">
                <option value="">Selecciona un centro...</option>
                {centersList.map(c => <option key={c} value={c}>{c}</option>)}
              </select>

              {qrBulkModal.loading ? (
                <div className="flex items-center justify-center gap-2 text-slate-400 text-sm py-4">
                  <Loader2 size={16} className="animate-spin" /> Cargando máquinas...
                </div>
              ) : qrBulkModal.center && (
                qrBulkModal.vehicles.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Este centro no tiene máquinas registradas.</p>
                ) : (
                  <>
                    <p className="text-sm text-slate-600 mb-3" data-testid="ws-qr-bulk-count">
                      <strong>{qrBulkModal.vehicles.length}</strong> máquina(s) en este centro.
                    </p>
                    <button data-testid="ws-qr-bulk-generate" onClick={generateAndPrintBulkQr} disabled={qrBulkModal.generating}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg text-sm disabled:opacity-50">
                      {qrBulkModal.generating ? <Loader2 size={15} className="animate-spin" /> : <Printer size={15} />}
                      {qrBulkModal.generating ? "Generando etiquetas..." : "Generar e imprimir etiquetas"}
                    </button>
                  </>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", mono = false, ...rest }) {
  return (
    <div>
      <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm ${mono ? "font-mono" : ""}`}
        {...rest}
      />
    </div>
  );
}

function TextArea({ label, testid, value, onChange, rows = 2 }) {
  return (
    <div>
      <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">{label}</label>
      <textarea
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm resize-none"
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = "#EA580C" }) {
  return (
    <div className="card-white p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + "18" }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div>
        <p className="text-xs text-slate-400 font-medium">{label}</p>
        <p className="text-2xl font-bold text-slate-800 leading-tight">{value ?? "—"}</p>
      </div>
    </div>
  );
}

function BatchStatusBadge({ status }) {
  const map = {
    processing: { label: "Procesando", cls: "bg-blue-50 text-blue-700", icon: Loader2, spin: true },
    completed: { label: "Completado", cls: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
    partial: { label: "Parcial", cls: "bg-amber-50 text-amber-700", icon: AlertTriangle },
    failed: { label: "Fallido", cls: "bg-red-50 text-red-700", icon: XCircle },
    cancelled: { label: "Cancelado", cls: "bg-slate-100 text-slate-500", icon: Ban },
  };
  const cfg = map[status] || map.processing;
  const Icon = cfg.icon;
  return (
    <span data-testid="ws-batch-status-badge" className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ml-2 ${cfg.cls}`}>
      <Icon size={11} className={cfg.spin ? "animate-spin" : ""} /> {cfg.label}
    </span>
  );
}
